import { COINS } from "../../../tiles";
import { TextButton } from "../../../ui/input/TextButton";
import { ComponentF, createElement } from "../../../ui/ui";
import { withColor } from "../../../ui/uiText";
import { runAbilityPrompts } from "../../abilities/abilityUtils";
import { Spell } from "../../actors/Spell";
import Colors from "../../Colors";
import { gameData } from "../../GameManager";
import { isDownKey, isUpKey } from "../keyHelper";

const HOTKEYS_AFTER_TEN = [
  "0",
  "!",
  "@",
  "#",
  "$",
  "%",
  "^",
  "&",
  "*",
  "(",
  ")",
];

const useSpell = (spell?: Spell | null) => {
  if (spell && (!spell.uses || 0 < spell.uses.current) && spell.ability) {
    const ability = spell.ability;
    runAbilityPrompts(ability, gameData.player).then((options) => {
      if (options && options.valid) {
        spell.uses?.change(-1);
        ability.apply(gameData.player, options);
      }
      gameData.selectedSpell = null;
    });
    return true;
  }
};

export const SpellBookView: ComponentF<void> = (C) => {
  const [state, setState] = C.useState({ focus: false, selected: 0 });

  if (gameData.selectedItem || gameData.target) {
    C.display.drawText(
      C.bounds.x,
      C.bounds.y,
      `${withColor(Colors.GRAY_2, "=")} ${withColor(Colors.GRAY_2, "Spells")}`
    );
  } else {
    C.display.drawText(
      C.bounds.x,
      C.bounds.y,
      `${withColor(Colors.GREEN_3, "=")} ${withColor(
        state.focus ? Colors.EGGSHELL : Colors.GRAY_3,
        "Spells"
      )}`
    );
  }

  let y = C.bounds.y + 2;
  const filtered = gameData.player.book.filter(
    (spell) => spell.listed === true
  );
  filtered.forEach((spell, index) => {
    const selected = state.focus && index === state.selected;
    const hasUses = spell.uses == null || spell.uses.current;
    const indexChar =
      index < 10 ? (index + 1).toString() : HOTKEYS_AFTER_TEN[index - 9];

    if (selected) {
      gameData.selectedSpell = spell;
    }
    C.display.drawText(
      C.bounds.x,
      y,
      withColor(hasUses ? Colors.GREEN_3 : Colors.GRAY_3, indexChar)
    );
    C.display.drawText(
      C.bounds.x +
        C.bounds.w -
        (spell.uses != null && spell.uses.max > 10 ? 5 : 3),
      y,
      spell.uses != null
        ? `${spell.uses.current.toString()}/${spell.uses.max}`
        : "-"
    );
    C.display.drawText(
      C.bounds.x + 2,
      y,
      withColor(
        selected
          ? hasUses
            ? Colors.AQUA_3
            : Colors.GRAY_2
          : state.focus
          ? hasUses
            ? Colors.EGGSHELL
            : Colors.GRAY_2
          : Colors.GRAY_3,
        spell.name
      ),
      C.bounds.w - 1
    );
    y += 1;
  });

  C.handlers.press = (p: [number, number]) => {
    if (gameData.selectedItem || gameData.target) {
      return;
    }
    if (state.focus && p[1] === 0) {
      gameData.selectedSpell = null;
      setState({ focus: false, selected: null });
      return;
    }

    const newSelected = p[1] - 2;
    if (newSelected >= 0 && newSelected < filtered.length) {
      setState({
        focus: true,
        selected: newSelected,
      });
    } else {
      setState({
        focus: true,
        selected: 0,
      });
    }
  };

  C.handlers.keypress = (key: string) => {
    if (gameData.selectedItem || gameData.target) {
      return false;
    }

    if (key === "=") {
      if (state.focus) {
        gameData.selectedSpell = null;
      }
      setState({ focus: !state.focus, selected: 0 });
      return true;
    }
    const code = key.charCodeAt(0);
    //1-9 characters
    if (48 < code && code <= 57) {
      const spell = filtered[code - 49];
      const spellUsed = useSpell(spell);
      if (spellUsed) {
        gameData.selectedSpell = spell;
        setState({ selected: 0, focus: false });
      }
    }

    const extendedIndex = HOTKEYS_AFTER_TEN.indexOf(key);
    if (extendedIndex !== -1) {
      const spell = filtered[extendedIndex + 10];
      const spellUsed = useSpell(spell);
      if (spellUsed) {
        gameData.selectedSpell = spell;
        setState({ selected: 0, focus: false });
      }
      return true;
    }

    if (48 <= code && code <= 57) {
      const spell = filtered[code - 48];
      const spellUsed = useSpell(spell);
      if (spellUsed) {
        setState({ selected: 0, focus: false });
      }
      return true;
    }

    if (state.focus) {
      if (key === "?") {
        return false;
      }
      if (isUpKey(key)) {
        setState({ selected: Math.max(0, state.selected - 1) });
      } else if (isDownKey(key)) {
        setState({
          selected: Math.min(filtered.length - 1, state.selected + 1),
        });
      } else if (key === "Escape") {
        gameData.selectedSpell = null;
        setState({ focus: false, selected: 0 });
      }
      return true;
    }
    return false;
  };

  if (gameData.selectedSpell && !gameData.target) {
    return [
      createElement(
        TextButton,
        { x: C.bounds.w - 10, y: C.bounds.h - 1, w: C.bounds.w, h: 1 },
        {
          title: "Cast",
          keyLabel: "Enter",
          keys: "Enter",
          disabled: gameData.selectedSpell.uses?.current === 0,
          onClick: () => {
            const spell = gameData.selectedSpell;
            const spellUsed = useSpell(spell);
            if (spellUsed) {
              setState({ selected: 0, focus: false });
            }
          },
        }
      ),
    ];
  }
};
